import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import ReadyToStartProject from "../components/ReadyToStartProject";
import Footer from "../components/Footer";
import AOS from "aos";
import "aos/dist/aos.css"; // Importez les styles de AOS
import { Helmet } from "react-helmet";

const SeoPresentation = () => {
  const [showMore, setShowMore] = useState({});

  const scrollToElement = (text) => {
    document.getElementById(text).scrollIntoView({ behavior: "smooth" });
  };

  const toggleShowMore = (index) => {
    setShowMore((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  useEffect(() => {
    AOS.init({
      duration: 1000, // Durée de l'animation en ms
      easing: "ease-in-out", // Type d'animation
    });
  }, []);

  return (
    <div className="bg-gray-900 min-h-screen text-white">
      <Helmet>
        <title>Révélez Votre Potentiel avec le SEO</title>
        <meta
          name="description"
          content="Augmentez la visibilité de votre site et attirez plus de clients grâce à des stratégies SEO efficaces."
        />
        <meta
          name="keywords"
          content="SEO, référencement, visibilité, optimisation, site web"
        />
      </Helmet>
      <Navbar />

      {/* Section d'introduction */}
      <section
        data-aos="fade-in" // Ajoutez l'animation AOS ici
        className="h-screen relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('/images/seo.jpg')` }}
      >
        <div className="flex items-center justify-center h-full bg-black bg-opacity-50">
          <div className="text-center text-white max-w-2xl px-6 md:px-12">
            <h1 className="text-5xl font-bold mb-6">
              Révélez Votre Potentiel avec le SEO
            </h1>
            <p className="text-2xl mb-6">
              Augmentez la visibilité de votre site et attirez plus de clients
              grâce à des stratégies SEO efficaces.
            </p>
            <p className="text-lg mb-8">
              Le SEO est une démarche essentielle pour améliorer votre
              classement sur les moteurs de recherche, attirer un trafic
              qualifié et offrir une meilleure expérience utilisateur.
            </p>
            <a
              href="#services"
              className="mt-4 px-6 py-3 bg-yellow-600 text-white text-xl font-semibold rounded-md shadow-md hover:bg-yellow-700 transition duration-300"
              onClick={() => scrollToElement("services")}
            >
              Découvrez nos services SEO
            </a>
          </div>
        </div>
        <div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white cursor-pointer animate-bounce"
          onClick={() => scrollToElement("why-seo")}
        >
          <i className="fas fa-chevron-down fa-3x"></i>
        </div>
      </section>

      {/* Section Pourquoi le SEO */}
      <section
        data-aos="fade-up"
        id="why-seo"
        className="py-16 px-6 md:px-12 lg:px-24 bg-gray-800"
      >
        <h2 className="text-4xl font-bold mb-8 text-center">
          Pourquoi le SEO ?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-semibold mb-4">
              Augmentez Votre Visibilité
            </h3>
            <p className="text-lg mb-4">
              Le SEO vous aide à être trouvé par vos clients potentiels lorsque
              qu'ils recherchent des produits ou services similaires aux vôtres.
            </p>
          </div>
          <div>
            <h3 className="text-2xl font-semibold mb-4">
              Attirez un Trafic Ciblé
            </h3>
            <p className="text-lg mb-4">
              En ciblant les bons mots-clés, vous attirez des visiteurs
              qualifiés qui sont plus susceptibles de devenir des clients.
            </p>
          </div>
          <div>
            <h3 className="text-2xl font-semibold mb-4">
              Améliorez l'Expérience Utilisateur
            </h3>
            <p className="text-lg mb-4">
              Une bonne optimisation SEO implique également des améliorations
              pour l'expérience utilisateur, rendant votre site plus agréable à
              naviguer.
            </p>
          </div>
          <div>
            <h3 className="text-2xl font-semibold mb-4">
              Obtenez des Résultats Durables
            </h3>
            <p className="text-lg mb-4">
              Contrairement à la publicité payante, les résultats du SEO sont
              durables et continuent d'apporter des bénéfices à long terme.
            </p>
          </div>
        </div>
      </section>

      <section
        data-aos="fade-up"
        id="services"
        className="py-16 bg-gray-900 px-6 md:px-12 lg:px-24"
      >
        <h2 className="text-4xl font-bold mb-8 text-center text-yellow-500">
          Nos Services SEO
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-gray-800 rounded-lg shadow-lg p-6 hover:shadow-2xl transition-shadow duration-300">
            <h3 className="text-2xl font-semibold mb-4 text-yellow-300">
              Audit SEO Complet
            </h3>
            <p className="text-lg mb-4">
              Nous analysons en profondeur votre site pour identifier les
              opportunités d'amélioration.
            </p>
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                showMore[0] ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <p className="text-lg mb-4">
                L'audit couvre l'analyse technique, l'évaluation des mots-clés,
                la structure du site, et bien plus encore pour garantir que
                votre site est optimisé pour les moteurs de recherche.
              </p>
            </div>
            <button
              onClick={() => toggleShowMore(0)}
              className="text-blue-500 hover:text-blue-600 transition duration-300"
            >
              {showMore[0] ? "Lire moins" : "Lire plus"} &rarr;
            </button>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-lg p-6 hover:shadow-2xl transition-shadow duration-300">
            <h3 className="text-2xl font-semibold mb-4 text-yellow-300">
              Optimisation On-Page
            </h3>
            <p className="text-lg mb-4">
              Optimisez les éléments sur votre site pour un meilleur classement
              dans les résultats de recherche.
            </p>
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                showMore[1] ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <p className="text-lg mb-4">
                Cela inclut l'optimisation des balises meta, des en-têtes, des
                URL, ainsi que l'amélioration de la vitesse de chargement et de
                l'expérience utilisateur.
              </p>
            </div>
            <button
              onClick={() => toggleShowMore(1)}
              className="text-blue-500 hover:text-blue-600 transition duration-300"
            >
              {showMore[1] ? "Lire moins" : "Lire plus"} &rarr;
            </button>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-lg p-6 hover:shadow-2xl transition-shadow duration-300">
            <h3 className="text-2xl font-semibold mb-4 text-yellow-300">
              Stratégie de Contenu
            </h3>
            <p className="text-lg mb-4">
              Développez un contenu pertinent et engageant pour attirer et
              retenir l'attention de vos visiteurs.
            </p>
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                showMore[2] ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <p className="text-lg mb-4">
                Nous créons un calendrier éditorial basé sur des recherches de
                mots-clés, et nous produisons des articles de blog, des pages de
                destination et d'autres contenus optimisés pour le SEO.
              </p>
            </div>
            <button
              onClick={() => toggleShowMore(2)}
              className="text-blue-500 hover:text-blue-600 transition duration-300"
            >
              {showMore[2] ? "Lire moins" : "Lire plus"} &rarr;
            </button>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-lg p-6 hover:shadow-2xl transition-shadow duration-300">
            <h3 className="text-2xl font-semibold mb-4 text-yellow-300">
              Link Building
            </h3>
            <p className="text-lg mb-4">
              Augmentez l'autorité de votre site avec des backlinks de qualité.
            </p>
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                showMore[3] ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <p className="text-lg mb-4">
                Nous mettons en place des stratégies pour acquérir des backlinks
                provenant de sites réputés, ce qui améliore le classement de
                votre site sur les moteurs de recherche.
              </p>
            </div>
            <button
              onClick={() => toggleShowMore(3)}
              className="text-blue-500 hover:text-blue-600 transition duration-300"
            >
              {showMore[3] ? "Lire moins" : "Lire plus"} &rarr;
            </button>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-lg p-6 hover:shadow-2xl transition-shadow duration-300">
            <h3 className="text-2xl font-semibold mb-4 text-yellow-300">
              SEO Local
            </h3>
            <p className="text-lg mb-4">
              Optimisez votre présence pour attirer des clients locaux et
              améliorer vos classements dans les recherches locales.
            </p>
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                showMore[4] ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <p className="text-lg mb-4">
                Nous nous concentrons sur l'optimisation de votre fiche Google
                My Business, l'acquisition de citations locales et
                l'amélioration de votre visibilité sur les cartes et annuaires
                locaux.
              </p>
            </div>
            <button
              onClick={() => toggleShowMore(4)}
              className="text-blue-500 hover:text-blue-600 transition duration-300"
            >
              {showMore[4] ? "Lire moins" : "Lire plus"} &rarr;
            </button>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-lg p-6 hover:shadow-2xl transition-shadow duration-300">
            <h3 className="text-2xl font-semibold mb-4 text-yellow-300">
              Analyse et Reporting
            </h3>
            <p className="text-lg mb-4">
              Suivez les performances de votre SEO avec des rapports détaillés
              et des analyses régulières.
            </p>
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                showMore[5] ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <p className="text-lg mb-4">
                Recevez des rapports complets avec des analyses sur les
                classements, le trafic, les conversions, et bien plus encore,
                pour ajuster et affiner votre stratégie SEO.
              </p>
            </div>
            <button
              onClick={() => toggleShowMore(5)}
              className="text-blue-500 hover:text-blue-600 transition duration-300"
            >
              {showMore[5] ? "Lire moins" : "Lire plus"} &rarr;
            </button>
          </div>
        </div>
      </section>

      {/* Section des Tarifs SEO */}
      <section
        id="pricing"
        data-aos="fade-up"
        className="py-20 bg-fixed bg-cover bg-center text-center"
        style={{
          backgroundImage: `url('/images/bg-pricing.jpg')`,
          backgroundAttachment: "fixed",
          backgroundPosition: "center",
        }}
      >
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 py-16">
          <h2 className="text-5xl font-bold mb-12 text-yellow-400">
            Nos Tarifs SEO
          </h2>
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 px-4">
            {/* Plan de Base */}
            <div className="bg-gray-800 border-2 border-blue-500 rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300 p-8 flex flex-col relative">
              <div className="absolute top-0 left-0 bg-blue-500 text-white py-2 px-6 text-sm font-bold transform -translate-y-1/2 -translate-x-1/4 rotate-12">
                Populaire
              </div>
              <h3 className="text-2xl font-semibold mb-6 text-yellow-300">
                Forfait de Base
              </h3>
              <p className="text-5xl font-bold mb-6 text-white">499€</p>
              <ul className="text-left space-y-4">
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Audit SEO complet
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Optimisation de 5 pages
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Rapport mensuel
                </li>
              </ul>
              <a
                href="/contact"
                className="mt-auto px-8 py-3 bg-blue-500 text-white font-semibold rounded-md shadow-lg hover:bg-blue-600 transition duration-300"
              >
                Commencer
              </a>
            </div>
            {/* Plan Avancé */}
            <div className="bg-gray-800 border-2 border-yellow-500 rounded-lg relative shadow-lg hover:shadow-2xl transition-shadow duration-300 p-8 flex flex-col">
              <div className="absolute top-0 left-0 bg-yellow-500 text-white py-2 px-6 text-sm font-bold transform -translate-y-1/2 -translate-x-1/4 rotate-12">
                Le plus Populaire
              </div>
              <h3 className="text-2xl font-semibold mb-6 text-yellow-300">
                Forfait Avancé
              </h3>
              <p className="text-5xl font-bold mb-6 text-white">999€</p>
              <ul className="text-left space-y-4">
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Audit SEO complet
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Optimisation de 15 pages
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Suivi des mots-clés
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Rapport hebdomadaire
                </li>
              </ul>
              <a
                href="/contact"
                className="mt-auto px-8 py-3 bg-yellow-500 text-white font-semibold rounded-md shadow-lg hover:bg-yellow-600 transition duration-300"
              >
                Commencer
              </a>
            </div>
            {/* Plan Pro */}
            <div className="bg-gray-800 relative border-2 border-red-500 rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300 p-8 flex flex-col">
              <div className="absolute top-0 left-0  bg-red-500 text-white py-2 px-6 text-sm font-bold transform -translate-y-1/2 -translate-x-1/4 rotate-12">
                Exclusif
              </div>
              <h3 className="text-2xl font-semibold mb-6 text-yellow-300">
                Forfait Pro
              </h3>
              <p className="text-5xl font-bold mb-6 text-white">1999€</p>
              <ul className="text-left space-y-4">
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Audit SEO complet
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Optimisation illimitée
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Suivi quotidien des mots-clés
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2 mb-5"></i>
                  Rapport quotidien détaillé
                </li>
              </ul>
              <a
                href="/contact"
                className="mt-auto px-8 py-3 bg-red-500 text-white font-semibold rounded-md shadow-lg hover:bg-red-600 transition duration-300"
              >
                Commencer
              </a>
            </div>
          </div>
          <p className="mt-10 text-gray-400 text-center">
            Les tarifs indiqués sont standards et peuvent varier en fonction de
            vos besoins spécifiques. N’hésitez pas à nous contacter pour un
            devis personnalisé.
          </p>
        </div>
      </section>

      <ReadyToStartProject />

      <Footer />
    </div>
  );
};

export default SeoPresentation;
